import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { DriverService } from '../driver/driver.service';
import { DriverPerformanceRow, VehicleProfitabilityRow } from '../fleet-analytics.model';
import { FleetAnalyticsService } from '../fleet-analytics.service';
import { VehicleService } from '../vehicle/vehicle.service';
import { SessionStorageService } from '../../services/session-storage.service';

export interface DropdownFilterOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-fleet-profitability',
  standalone: true,
  templateUrl: './fleet-profitability.component.html',
  styleUrls: ['./fleet-profitability.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    DatePickerModule,
    SelectModule,
    TableModule,
    TagModule,
    ChartModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService]
})
export class FleetProfitabilityComponent implements OnInit {
  isLoading = false;
  vehicleRows: VehicleProfitabilityRow[] = [];
  driverRows: DriverPerformanceRow[] = [];
  dateRange: Date[] = [this.addDays(new Date(), -30), new Date()];
  filters = {
    resourceSource: '',
    driverId: undefined as number | undefined,
    vehicleId: undefined as number | undefined,
    vehicleTypeId: undefined as number | undefined
  };

  readonly sourceOptions = [
    { label: 'All sources', value: '' },
    { label: 'Owned', value: 'OWNED' },
    { label: 'Rented', value: 'RENTAL' },
    { label: 'Freelance', value: 'FREELANCE' }
  ];

  driverOptions: DropdownFilterOption[] = [];
  vehicleOptions: DropdownFilterOption[] = [];
  vehicleTypeOptions: DropdownFilterOption[] = [];

  // Chart data models
  sourcePnlChartData: any;
  costBreakdownChartData: any;
  vehicleRankingChartData: any;
  driverContributionChartData: any;

  // Chart display options
  groupedBarChartOptions: any;
  doughnutChartOptions: any;
  horizontalBarChartOptions: any;
  driverBarChartOptions: any;

  get hasVehicleData(): boolean {
    return this.vehicleRows.length > 0;
  }

  get hasDriverData(): boolean {
    return this.driverRows.length > 0;
  }

  get metrics(): PageMetric[] {
    const revenue = this.sum(this.vehicleRows, 'shipmentRevenue');
    const totalCost = this.sum(this.vehicleRows, 'totalCost');
    const profit = this.sum(this.vehicleRows, 'profit');
    const deliveries = this.sum(this.vehicleRows, 'deliveryCount');
    return [
      { label: 'Revenue', value: this.money(revenue), hint: 'Operational', tone: 'info' },
      { label: 'Total Cost', value: this.money(totalCost), hint: 'Direct + fleet costs', tone: 'warn' },
      { label: 'Profit', value: this.money(profit), hint: this.margin(profit, revenue), tone: profit < 0 ? 'danger' : 'success' },
      { label: 'Deliveries', value: deliveries, hint: 'In selected period', tone: 'default' }
    ];
  }

  constructor(
    private fleetAnalyticsService: FleetAnalyticsService,
    private vehicleService: VehicleService,
    private driverService: DriverService,
    private sessionStorageService: SessionStorageService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initChartOptions();
    this.loadFilterOptions();
    this.load();
  }

  load(): void {
    const filters = {
      ...this.filters,
      periodFrom: this.dateRange?.[0],
      periodTo: this.dateRange?.[1]
    };
    this.isLoading = true;
    forkJoin({
      vehicles: this.fleetAnalyticsService.getVehicleProfitability(filters),
      drivers: this.fleetAnalyticsService.getDriverPerformance(filters)
    }).pipe(finalize(() => this.isLoading = false)).subscribe({
      next: result => {
        this.vehicleRows = result.vehicles.items ?? [];
        this.driverRows = result.drivers.items ?? [];
        this.syncOptionsFromRows();
        this.buildCharts();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Unable to load fleet profitability.' })
    });
  }

  onPeriodChange(): void {
    if (this.dateRange?.[0] && this.dateRange?.[1]) {
      this.load();
    }
  }

  clearFilters(): void {
    this.filters = {
      resourceSource: '',
      driverId: undefined,
      vehicleId: undefined,
      vehicleTypeId: undefined
    };
    this.dateRange = [this.addDays(new Date(), -30), new Date()];
    this.load();
  }

  sourceSeverity(source?: string): 'success' | 'info' | 'warn' | 'secondary' {
    switch (source) {
      case 'OWNED': return 'success';
      case 'RENTAL': return 'warn';
      case 'FREELANCE': return 'info';
      default: return 'secondary';
    }
  }

  money(value?: number): string {
    return `${Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} SAR`;
  }

  percent(value?: number): string {
    return `${Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }

  private margin(profit: number, revenue: number): string {
    if (!revenue) {
      return '0% margin';
    }
    return `${((profit / revenue) * 100).toFixed(2)}% margin`;
  }

  private sum<T>(rows: T[], key: keyof T): number {
    return rows.reduce((total, row) => total + Number(row[key] ?? 0), 0);
  }

  private addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }

  private loadFilterOptions(): void {
    const types = this.sessionStorageService.getVehicleTypes() ?? [];
    this.vehicleTypeOptions = types
      .filter(t => t.id != null)
      .map(t => ({
        label: t.name || `Type #${t.id}`,
        value: t.id!
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const company = this.sessionStorageService.getCompany();
    const companyId = company?.id ?? 0;
    const agencyId = company?.transportationAgencyId ?? this.sessionStorageService.getTransportationAgency()?.rowId ?? 0;

    if (companyId) {
      forkJoin({
        owned: agencyId
          ? this.vehicleService.findVehicleMinsByTransportationAgencyId(agencyId).pipe(catchError(() => of({ items: [] })))
          : of({ items: [] }),
        freelance: this.vehicleService.getFreelanceVehicleMinsByCompanyId(companyId).pipe(catchError(() => of({ items: [] }))),
        rental: this.vehicleService.getRentalVehicleMinsByCompanyId(companyId).pipe(catchError(() => of({ items: [] })))
      }).subscribe(res => {
        const allVehicles = [
          ...(res.owned.items ?? []),
          ...(res.freelance.items ?? []),
          ...(res.rental.items ?? [])
        ];
        this.mergeVehicleOptions(allVehicles);
      });

      forkJoin({
        owned: agencyId
          ? this.driverService.findDriversByTransportationAgencyId(0, 500, agencyId).pipe(catchError(() => of({ items: [] })))
          : of({ items: [] }),
        freelance: this.driverService.getFreelanceDriversByCompanyId(0, 500, companyId).pipe(catchError(() => of({ items: [] }))),
        rental: this.driverService.getRentalDriversByCompanyId(0, 500, companyId).pipe(catchError(() => of({ items: [] })))
      }).subscribe(res => {
        const allDrivers = [
          ...(res.owned.items ?? []),
          ...(res.freelance.items ?? []),
          ...(res.rental.items ?? [])
        ];
        this.mergeDriverOptions(allDrivers);
      });
    }
  }

  private mergeVehicleOptions(vehicles: Array<{ vehicleId?: number; plateNumber?: string; registrationNumber?: string }>): void {
    const existing = new Map(this.vehicleOptions.map(o => [o.value, o]));
    vehicles.forEach(v => {
      if (v.vehicleId && !existing.has(v.vehicleId)) {
        const plate = (v.plateNumber || '').trim();
        const reg = (v.registrationNumber || '').trim();
        const label = plate
          ? `${plate}${reg && reg !== '-' ? ' (' + reg + ')' : ''}`
          : `Vehicle #${v.vehicleId}`;
        existing.set(v.vehicleId, { label, value: v.vehicleId });
      }
    });
    this.vehicleOptions = Array.from(existing.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  private mergeDriverOptions(drivers: Array<{ id?: number; driverId?: number; name?: string; legalName?: string }>): void {
    const existing = new Map(this.driverOptions.map(o => [o.value, o]));
    drivers.forEach(d => {
      const id = d.id ?? d.driverId;
      if (id && !existing.has(id)) {
        const name = (d.name || d.legalName || '').trim();
        const label = name ? name : `Driver #${id}`;
        existing.set(id, { label, value: id });
      }
    });
    this.driverOptions = Array.from(existing.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  private syncOptionsFromRows(): void {
    this.mergeVehicleOptions(this.vehicleRows);
    this.mergeDriverOptions(this.driverRows.map(r => ({ id: r.driverId, name: r.driverName })));

    const existingTypes = new Map(this.vehicleTypeOptions.map(o => [o.value, o]));
    let typesChanged = false;
    this.vehicleRows.forEach(r => {
      if (r.vehicleTypeId && !existingTypes.has(r.vehicleTypeId)) {
        existingTypes.set(r.vehicleTypeId, { label: `Type #${r.vehicleTypeId}`, value: r.vehicleTypeId });
        typesChanged = true;
      }
    });
    if (typesChanged) {
      this.vehicleTypeOptions = Array.from(existingTypes.values()).sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  private initChartOptions(): void {
    const textColor = '#334155';
    const textMuted = '#64748b';
    const gridColor = '#f1f5f9';

    this.groupedBarChartOptions = {
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: textColor, boxWidth: 12, usePointStyle: true, font: { weight: '600' } }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => ` ${context.dataset.label}: ${Number(context.raw || 0).toLocaleString()} SAR`
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: textMuted, font: { weight: '600' } }
        },
        y: {
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: {
            color: textMuted,
            callback: (val: number) => `${Number(val).toLocaleString()} SAR`
          }
        }
      }
    };

    this.doughnutChartOptions = {
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: textColor, boxWidth: 12, usePointStyle: true, font: { weight: '500' } }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const val = Number(context.raw || 0);
              const total = (context.chart._metasets?.[0]?.total as number) || 1;
              const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
              return ` ${label}: ${val.toLocaleString()} SAR (${pct}%)`;
            }
          }
        }
      }
    };

    this.horizontalBarChartOptions = {
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const profit = Number(context.raw || 0);
              const tone = profit >= 0 ? 'Profit' : 'Loss';
              return ` Net ${tone}: ${profit.toLocaleString()} SAR`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: { color: gridColor },
          ticks: {
            color: textMuted,
            callback: (val: number) => `${Number(val).toLocaleString()} SAR`
          }
        },
        y: {
          grid: { display: false },
          ticks: { color: textColor, font: { weight: '600' } }
        }
      }
    };

    this.driverBarChartOptions = {
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: any) => ` Contribution: ${Number(context.raw || 0).toLocaleString()} SAR`
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: textMuted, font: { weight: '600' } }
        },
        y: {
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: {
            color: textMuted,
            callback: (val: number) => `${Number(val).toLocaleString()} SAR`
          }
        }
      }
    };
  }

  private buildCharts(): void {
    this.buildSourcePnlChart();
    this.buildCostBreakdownChart();
    this.buildVehicleRankingChart();
    this.buildDriverContributionChart();
  }

  private buildSourcePnlChart(): void {
    const sources = ['OWNED', 'RENTAL', 'FREELANCE'];
    const labels = ['Owned', 'Rented', 'Freelance'];

    const revenue = sources.map(src =>
      this.vehicleRows
        .filter(r => (r.resourceSource || 'OWNED').toUpperCase() === src)
        .reduce((sum, r) => sum + Number(r.shipmentRevenue || 0), 0)
    );

    const cost = sources.map(src =>
      this.vehicleRows
        .filter(r => (r.resourceSource || 'OWNED').toUpperCase() === src)
        .reduce((sum, r) => sum + Number(r.totalCost || 0), 0)
    );

    const profit = sources.map(src =>
      this.vehicleRows
        .filter(r => (r.resourceSource || 'OWNED').toUpperCase() === src)
        .reduce((sum, r) => sum + Number(r.profit || 0), 0)
    );

    this.sourcePnlChartData = {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenue,
          backgroundColor: '#0f766e',
          borderRadius: 6
        },
        {
          label: 'Total Cost',
          data: cost,
          backgroundColor: '#f59e0b',
          borderRadius: 6
        },
        {
          label: 'Net Profit',
          data: profit,
          backgroundColor: profit.map(p => (p >= 0 ? '#10b981' : '#ef4444')),
          borderRadius: 6
        }
      ]
    };
  }

  private buildCostBreakdownChart(): void {
    const tripCost = this.sum(this.vehicleRows, 'directTripCost');
    const fuelCost = this.sum(this.vehicleRows, 'fuelCost');
    const maintenanceCost = this.sum(this.vehicleRows, 'maintenanceCost');
    const rentalCost = this.sum(this.vehicleRows, 'rentalCost');
    const workforceOther = this.vehicleRows.reduce(
      (sum, r) => sum + Number(r.workforceCashCost || 0) + Number(r.mobilizationCost || 0),
      0
    );

    const items = [
      { label: 'Direct Trip', value: tripCost, color: '#2563eb' },
      { label: 'Fuel', value: fuelCost, color: '#f59e0b' },
      { label: 'Maintenance', value: maintenanceCost, color: '#ef4444' },
      { label: 'Rental', value: rentalCost, color: '#8b5cf6' },
      { label: 'Workforce & Other', value: workforceOther, color: '#64748b' }
    ];

    const totalCost = items.reduce((acc, curr) => acc + curr.value, 0);
    const visible = totalCost > 0 ? items.filter(i => i.value > 0) : items;

    this.costBreakdownChartData = {
      labels: visible.map(i => i.label),
      datasets: [
        {
          data: visible.map(i => i.value),
          backgroundColor: visible.map(i => i.color),
          borderWidth: 0,
          hoverOffset: 4
        }
      ]
    };
  }

  private buildVehicleRankingChart(): void {
    const sorted = [...this.vehicleRows].sort((a, b) => Number(b.profit || 0) - Number(a.profit || 0));

    const positive = sorted.filter(v => Number(v.profit || 0) > 0);
    const negative = sorted.filter(v => Number(v.profit || 0) < 0);

    let candidates: VehicleProfitabilityRow[] = [];
    if (positive.length > 0 && negative.length > 0) {
      const topPros = positive.slice(0, 4);
      const topLoss = negative.slice(-4);
      candidates = [...topPros, ...topLoss];
    } else {
      candidates = sorted.slice(0, 8);
    }

    this.vehicleRankingChartData = {
      labels: candidates.map(v => v.plateNumber || `Vehicle #${v.vehicleId}`),
      datasets: [
        {
          label: 'Net Profit',
          data: candidates.map(v => Number(v.profit || 0)),
          backgroundColor: candidates.map(v => (Number(v.profit || 0) >= 0 ? '#10b981' : '#ef4444')),
          borderRadius: 4
        }
      ]
    };
  }

  private buildDriverContributionChart(): void {
    const active = [...this.driverRows]
      .filter(d => (d.deliveryCount || 0) > 0 || (d.profitContribution || 0) !== 0)
      .sort((a, b) => Number(b.profitContribution || 0) - Number(a.profitContribution || 0))
      .slice(0, 7);

    this.driverContributionChartData = {
      labels: active.map(d => d.driverName || `Driver #${d.driverId}`),
      datasets: [
        {
          label: 'Profit Contribution',
          data: active.map(d => Number(d.profitContribution || 0)),
          backgroundColor: active.map(d => (Number(d.profitContribution || 0) >= 0 ? '#0ea5e9' : '#f87171')),
          borderRadius: 6
        }
      ]
    };
  }
}
